import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import {createSellerMediaApi} from '../Media/mediaApi';
import ContentEditorFeature from './ContentEditorFeature';
import DisplaySettingsSection from './DisplaySettingsSection';
import ConversionPreview from './editor/ConversionPreview';
import InlineMediaPicker from './editor/InlineMediaPicker';
import SourceEditor from './editor/SourceEditor';
import BlogComments from './comments/BlogComments';
import ContentGallery from './media/ContentGallery';
import FeaturedMedia from './media/FeaturedMedia';
import MastheadImage from './media/MastheadImage';
import YouTubeManager from './media/YouTubeManager';
import MetadataEditor from './metadata/MetadataEditor';
import BlogPublishing from './publishing/BlogPublishing';
import PreviewModal from './PreviewModal';
import createContentApi from './contentApi';
import {contentConfig} from './contentConfig';
import useContentDraft from './hooks/useContentDraft';
import './contentEditor.css';

function makeDraft(resource) {
    return {
        translations: Object.fromEntries([...new Set([
            ...Object.keys(resource.translations || {}),
            ...Object.keys(resource.contentTranslations || {}),
        ])].map(locale => [locale, {
            title: resource.translations?.[locale]?.title || '',
            ...(resource.isBlog ? {excerpt: resource.translations?.[locale]?.excerpt || ''} : {}),
            content: {
                content: resource.contentTranslations?.[locale]?.content || '',
                format: resource.contentTranslations?.[locale]?.format || resource.contentFormat || 'html',
                preferredMode: resource.contentTranslations?.[locale]?.preferredMode
                    || resource.preferredMode || 'richText',
                inlineMediaIds: resource.contentTranslations?.[locale]?.inlineMediaIds || [],
            },
        }])),
        title: resource.title || '',
        slug: resource.slug || '',
        requiredSubscriptionId: resource.requiredSubscriptionId || null,
        ...(resource.isBlog ? {
            excerpt: resource.excerpt || '',
            allowComments: resource.allowComments || false,
            imageId: resource.imageId || null,
            videoId: resource.videoId || null,
        } : {}),
        mastheadImageId: resource.mastheadImageId || null,
        content: {
            content: resource.content || '',
            format: resource.contentFormat || 'markdown',
            preferredMode: resource.preferredMode || 'markdown',
            htmlPolicy: resource.htmlPolicy === 'legacy' ? 'standard' : resource.htmlPolicy,
            inlineMediaIds: resource.inlineMediaIds || [],
        },
        display: {
            mode: resource.displayMode || 'standard',
            template: resource.displayTemplate || 'classic',
        },
        secondaryContent: {
            content: resource.secondaryContent?.content || '',
            format: resource.secondaryContent?.contentFormat || 'markdown',
            preferredMode: resource.secondaryContent?.preferredMode || 'markdown',
            htmlPolicy: resource.secondaryContent?.htmlPolicy === 'legacy'
                ? 'standard'
                : (resource.secondaryContent?.htmlPolicy || 'standard'),
            inlineMediaIds: resource.secondaryContent?.inlineMediaIds || [],
        },
        seo: resource.seo || {},
    };
}

function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, character => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[character]);
}

function ContentEditorLoaded({
    api,
    mediaApi,
    kind,
    contentUuid,
    initialEnvelope,
    initialEtag,
    backUrl,
    classicUrl,
    onReload,
}) {
    const config = contentConfig(kind);
    const [resource, setResource] = useState(initialEnvelope.resource);
    const [conversion, setConversion] = useState(null);
    const [conversionLoading, setConversionLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pendingInlineFile, setPendingInlineFile] = useState(null);
    const [notice, setNotice] = useState('');
    const [operationError, setOperationError] = useState('');
    const [comments, setComments] = useState(initialEnvelope.comments?.data || []);
    const [commentsMeta, setCommentsMeta] = useState(initialEnvelope.comments?.meta || {});
    const [activeContentSegment, setActiveContentSegment] = useState('primary');

    const languages = initialEnvelope.editor.languages;
    const defaultLanguage = languages.defaultLanguage;
    const [activeLanguage, setActiveLanguage] = useState(defaultLanguage);
    const persistedDraft = useRef(makeDraft(initialEnvelope.resource));
    const savedEnvelope = useRef(initialEnvelope);
    const saveProgress = useRef(null);
    const saveDraft = useCallback(async (nextDraft, nextRevision, intent) => {
        const {translations, ...primary} = nextDraft;
        const {translations: savedTranslations, ...savedPrimary} = persistedDraft.current;
        let result = {data: savedEnvelope.current, etag: nextRevision};
        const advance = next => {
            result = next;
            savedEnvelope.current = next.data;
            // Every successful write rotates the same revision, even if a later locale fails.
            saveProgress.current?.(next);
        };
        if (JSON.stringify(primary) !== JSON.stringify(savedPrimary)) {
            advance(await api.saveDraft(kind, contentUuid, result.etag, primary, intent));
            persistedDraft.current = {...primary, translations: persistedDraft.current.translations};
        }
        for (const [locale, translation] of Object.entries(translations)) {
            if (JSON.stringify(translation) === JSON.stringify(savedTranslations[locale])) continue;
            advance(await api.saveTranslation(kind, contentUuid, locale, result.etag, translation));
            persistedDraft.current = {
                ...persistedDraft.current,
                translations: {...persistedDraft.current.translations, [locale]: translation},
            };
        }
        return result;
    }, [api, contentUuid, kind]);
    const draftState = useContentDraft({
        initialDraft: makeDraft(initialEnvelope.resource),
        initialRevision: initialEtag,
        saveDraft,
        onSaved: envelope => setResource(envelope.resource),
    });
    const {
        draft,
        revision,
        status,
        error,
        conflict,
        setDraft,
        saveNow,
        replaceDraft,
        retryConflict,
    } = draftState;

    saveProgress.current = result => {
        draftState.setRevision(result.etag);
        setResource(result.data.resource);
    };
    const isTranslation = activeLanguage !== defaultLanguage;
    const activeLanguageLabel = languages.languageOptions
        .find(option => option.locale === activeLanguage)?.label || activeLanguage;
    const emptyTranslation = {
        title: '',
        ...(resource.isBlog ? {excerpt: ''} : {}),
        content: {content: '', format: 'html', preferredMode: 'richText', inlineMediaIds: []},
    };
    const activeDraft = isTranslation ? draft.translations[activeLanguage] || emptyTranslation : draft;
    const activeSegment = isTranslation ? 'primary' : activeContentSegment;
    const activeContent = activeSegment === 'secondary' ? draft.secondaryContent : activeDraft.content;
    const updateWriting = updater => setDraft(current => {
        if (!isTranslation) return updater(current);
        const translation = current.translations[activeLanguage] || emptyTranslation;
        return {...current, translations: {
            ...current.translations, [activeLanguage]: updater(translation),
        }};
    });
    const updateContent = changes => {
        if (!isTranslation && activeSegment === 'secondary') {
            setDraft(current => ({...current, secondaryContent: {...current.secondaryContent, ...changes}}));
        } else updateWriting(current => ({...current, content: {...current.content, ...changes}}));
    };

    const updateEnvelope = useCallback((result, message = '') => {
        persistedDraft.current = makeDraft(result.data.resource);
        savedEnvelope.current = result.data;
        setResource(result.data.resource);
        replaceDraft(makeDraft(result.data.resource), result.etag);
        if (message) setNotice(message);
        return result.data;
    }, [replaceDraft]);

    const runMutation = useCallback(async (operation, message = '') => {
        setOperationError('');
        const saved = await saveNow('workflow');
        const currentRevision = saved?.etag || revision;
        try {
            const result = await operation(currentRevision);
            return updateEnvelope(result, message);
        } catch (nextError) {
            setOperationError(nextError.message);
            throw nextError;
        }
    }, [revision, saveNow, updateEnvelope]);

    const requestConversion = async target => {
        setConversionLoading(true);
        setOperationError('');
        try {
            const source = activeContent;
            const saved = await saveNow('manual');
            const result = await api.convert(kind, contentUuid, saved.etag, {
                content: source.content,
                from: source.format,
                to: target.format,
            });
            setConversion({...result.data, mode: target.mode, sourceContent: source.content, segment: activeSegment, locale: activeLanguage});
        } catch (nextError) {
            setOperationError(nextError.message);
        } finally {
            setConversionLoading(false);
        }
    };

    const [mediaTarget, setMediaTarget] = useState(null);
    const openImagePicker = () => {
        setMediaTarget({locale: activeLanguage, segment: activeSegment});
        setPickerOpen(true);
    };
    const insertInlineImage = (file, altText) => {
        const target = mediaTarget || {locale: activeLanguage, segment: activeSegment};
        const source = file.cdnUrl || file.url;
        setDraft(current => {
            const translated = target.locale !== defaultLanguage;
            const writing = translated ? current.translations[target.locale] || emptyTranslation : current;
            const key = target.segment === 'secondary' && !translated ? 'secondaryContent' : 'content';
            const content = writing[key];
            const markup = content.format === 'markdown'
                ? `\n![${String(altText).replace(/[[\]]/g, '')}](${source})\n`
                : `\n<img src="${escapeHtml(source)}" alt="${escapeHtml(altText)}" data-file-id="${file.id}">\n`;
            const updated = {...writing, [key]: {
                ...content, content: `${content.content}${markup}`,
                inlineMediaIds: [...new Set([...(content.inlineMediaIds || []), file.id])],
            }};
            return translated ? {...current, translations: {
                ...current.translations, [target.locale]: updated,
            }} : updated;
        });
        setPickerOpen(false);
        setPendingInlineFile(null);
        setNotice('Image inserted. The draft will autosave.');
    };

    const uploadPastedImage = async file => {
        setMediaTarget({locale: activeLanguage, segment: activeSegment});
        setOperationError('');
        try {
            const uploaded = await mediaApi.upload(file);
            setPendingInlineFile(uploaded);
            setPickerOpen(true);
        } catch (nextError) {
            setOperationError(nextError.message);
        }
    };

    const changeDisplay = nextDisplay => setDraft(current => {
        const enteringCustomHtml = nextDisplay.template === 'customHtml'
            && current.display?.template !== 'customHtml';
        const leavingCustomHtml = nextDisplay.template !== 'customHtml'
            && current.display?.template === 'customHtml';
        if (!enteringCustomHtml && !leavingCustomHtml) {
            return {...current, display: nextDisplay};
        }
        return {
            ...current,
            display: nextDisplay,
            ...(enteringCustomHtml ? {confirmAdvancedHtml: true} : {}),
            content: {
                ...current.content,
                htmlPolicy: enteringCustomHtml ? 'takeover' : 'standard',
            },
        };
    });

    const loadComments = async (page = 1, append = false) => {
        const result = await api.loadComments(contentUuid, page);
        setComments(current => append ? [...current, ...result.data] : result.data);
        setCommentsMeta(result.meta);
    };

    const gallery = resource.gallery?.gallery || [];
    const youtube = resource.youtube?.videos || [];
    const displayTemplates = initialEnvelope.editor.displayTemplates || [];
    const selectedTemplate = displayTemplates
        .find(candidate => candidate.id === draft.display?.template);
    const needsSecondaryContent = selectedTemplate?.needsSecondaryContent === true;
    const usesMasthead = ['masthead', 'twoColumnMasthead']
        .includes(draft.display?.template);

    useEffect(() => {
        if (!needsSecondaryContent && activeContentSegment !== 'primary') {
            setActiveContentSegment('primary');
        }
    }, [activeContentSegment, needsSecondaryContent]);
    const saveStatusLabel = {
        clean: 'All changes saved',
        dirty: 'Unsaved changes',
        saving: 'Saving…',
        saved: 'Saved',
        offline: 'Offline — changes are still here',
        invalid: 'Save failed',
        conflict: 'Conflict — this content changed elsewhere',
    }[status];

    return <main className="content-editor-shell">
        <header className="content-editor-topbar">
            <a className="button secondary" href={backUrl || '#'}>{config.backLabel}</a>
            <div className="content-editor-heading">
                <span>{config.label}{isTranslation ? ` · ${activeLanguageLabel}` : ''}</span>
                <strong>{activeDraft.title || draft.title || `Untitled ${config.label.toLowerCase()}`}</strong>
                <small className={`content-save-status is-${status}`} role="status" aria-live="polite">
                    {saveStatusLabel}
                </small>
            </div>
            <div className="content-editor-topbar-actions">
                <button type="button" className="button" disabled={status === 'saving'}
                    onClick={() => saveNow().catch(() => undefined)}>Save</button>
                <button type="button" className="button secondary"
                    onClick={async () => {
                        setOperationError('');
                        try {
                            const saved = await saveNow('preview');
                            const currentRevision = saved?.etag || revision;
                            const result = await api.preview(
                                kind,
                                contentUuid,
                                currentRevision,
                                activeLanguage,
                            );
                            setPreviewUrl(result.data.url);
                        } catch (nextError) {
                            setOperationError(nextError.message);
                        }
                    }}>
                    Preview
                </button>
                {resource.isPublished
                    ? <button type="button" className="button secondary"
                        onClick={() => runMutation(
                            current => api.unpublish(kind, contentUuid, current),
                            'Content unpublished.',
                        ).catch(() => undefined)}>Unpublish</button>
                    : <button type="button" className="button"
                        onClick={() => runMutation(
                            current => api.publish(kind, contentUuid, current),
                            'Publication request saved.',
                        ).catch(() => undefined)}>Publish</button>}
            </div>
        </header>

        {(notice || operationError || error) && <div
            className={`flash ${operationError || error ? 'error' : 'success'}`}
            role={operationError || error ? 'alert' : 'status'}>
            {operationError || error?.message || notice}
        </div>}
        {conflict && <div className="flash error content-conflict" role="alert">
            <h2>This content changed elsewhere</h2>
            <p>Your unsaved draft is still in this browser. Reload the current server version or copy
                your source before deciding what to keep.</p>
            <button type="button" className="button" onClick={() => {
                replaceDraft(makeDraft(resource), revision);
                onReload();
            }}>Reload server version</button>
            <button type="button" className="button secondary" onClick={() => {
                const source = isTranslation ? activeDraft.content.content : needsSecondaryContent
                    ? `Main column:\n${draft.content.content}\n\nSecond column:\n${
                        draft.secondaryContent.content}`
                    : draft.content.content;
                navigator.clipboard?.writeText(source);
                setNotice('Unsaved content copied.');
            }}>Copy unsaved content</button>
            {conflict.revision && <button type="button" className="button secondary"
                onClick={() => retryConflict().catch(() => undefined)}>
                Keep my draft and retry
            </button>}
            {conflict.revision && <p className="help">
                Retrying keeps this complete local draft and replaces the newer saved draft.
            </p>}
        </div>}

        <div className="content-editor-layout">
            <section className="content-editor-main" aria-label={`${config.label} draft`}>
                <div className="content-writing-language">
                    <div>
                        <label htmlFor="content-language">Writing language</label>
                        <select id="content-language" value={activeLanguage}
                            onChange={event => {
                                setActiveLanguage(event.target.value);
                                setActiveContentSegment('primary');
                            }}>
                            {languages.languageOptions.map(option => <option key={option.locale}
                                value={option.locale}>{option.label}{option.locale === defaultLanguage
                                    ? ' (default)' : ''}</option>)}
                        </select>
                    </div>
                    <p className="help">{isTranslation
                        ? `Editing the ${activeLanguageLabel} translation. Save and preview work for this language.`
                        : 'Write your content here. Changes save automatically.'}</p>
                </div>
                {isTranslation && !draft.translations[activeLanguage] && <p className="content-language-note">
                    No translation yet. Readers see the default language until you add one.
                </p>}
                <label htmlFor="content-title">Title</label>
                <input id="content-title" type="text" value={activeDraft.title}
                    placeholder={isTranslation ? draft.title : ''}
                    onChange={event => updateWriting(current => ({...current, title: event.target.value}))} />
                {resource.isBlog && <>
                    <label htmlFor="content-excerpt">Excerpt</label>
                    <textarea id="content-excerpt" rows="2" value={activeDraft.excerpt}
                        onChange={event => updateWriting(current => ({...current, excerpt: event.target.value}))} />
                </>}
                <div className="content-editor-content-section">
                    <div className="content-editor-content-heading">
                        <h2>{!isTranslation && draft.display?.template === 'customHtml'
                            ? 'Full page HTML' : needsSecondaryContent
                                ? activeSegment === 'primary' ? 'Main column' : 'Second column'
                                : 'Content'}</h2>
                        {needsSecondaryContent && !isTranslation && <div className="content-segment-toggle"
                            role="tablist" aria-label="Content column">
                            {['primary', 'secondary'].map(segment => <button key={segment}
                                type="button" role="tab" aria-selected={activeSegment === segment}
                                onClick={() => setActiveContentSegment(segment)}>
                                {segment === 'primary' ? 'Main column' : 'Second column'}
                            </button>)}
                        </div>}
                    </div>
                    {conversionLoading && <p role="status">Preparing conversion preview…</p>}
                    {!isTranslation && draft.display?.template === 'customHtml'
                        ? <>
                            <div className="content-editor-toolbar" role="toolbar" aria-label="Source tools">
                                <button type="button" className="content-editor-tool-button"
                                    onClick={openImagePicker}>Insert image</button>
                            </div>
                            <SourceEditor value={activeContent.content} mode="html" label="Custom page HTML"
                                onChange={value => updateContent({content: value})} />
                        </>
                        : <ContentEditorFeature key={`${activeLanguage}:${activeSegment}`}
                            content={activeContent.content}
                            mode={activeContent.preferredMode}
                            format={activeContent.format}
                            onChange={value => updateContent({content: value})}
                            onModeChange={preferredMode => updateContent({preferredMode})}
                            onConversionRequired={requestConversion}
                            onPasteImage={uploadPastedImage}
                            onInsertImage={openImagePicker}
                        />}
                </div>
                {isTranslation && draft.translations[activeLanguage] && <div className="content-translation-actions">
                    <button type="button" className="content-text-action" onClick={() => {
                        if (!window.confirm(`Remove the ${activeLanguageLabel} translation? Readers will see the default language.`)) return;
                        runMutation(current => api.deleteTranslation(kind, contentUuid, activeLanguage, current),
                            'Translation removed.').catch(() => undefined);
                    }}>Remove this translation</button>
                </div>}
            </section>

            <div className="content-editor-settings" aria-label="Page settings">
                <details className="content-settings-group">
                    <summary>Publishing &amp; access</summary>
                <section className="content-editor-panel" aria-labelledby="content-access-title">
                    <h2 id="content-access-title">Access</h2>
                    <label htmlFor="content-subscription">Who can read this?</label>
                    <select id="content-subscription" value={draft.requiredSubscriptionId || ''}
                        onChange={event => setDraft(current => ({
                            ...current,
                            requiredSubscriptionId: event.target.value || null,
                        }))}>
                        <option value="">Everyone</option>
                        {initialEnvelope.editor.subscriptionChoices.map(product => <option
                            key={product.uuid}
                            value={product.uuid}
                        >Subscribers to {product.name}</option>)}
                    </select>
                    {resource.isBlog && <>
                        <label htmlFor="content-comments-access">Who can comment?</label>
                        <select id="content-comments-access"
                            value={draft.allowComments === 'subscribers'
                                ? 'subscribers'
                                : draft.allowComments ? 'everyone' : 'disabled'}
                            onChange={event => setDraft(current => ({
                                ...current,
                                allowComments: event.target.value === 'subscribers'
                                    ? 'subscribers'
                                    : event.target.value === 'everyone',
                            }))}>
                            <option value="disabled">Comments disabled</option>
                            <option value="everyone">Everyone who can read</option>
                            <option value="subscribers">Subscribers only</option>
                        </select>
                    </>}
                </section>
                {resource.isBlog && <BlogPublishing
                    resource={resource}
                    redReviewEnabled={initialEnvelope.editor.capabilities.redReview}
                    onRequestReview={() => runMutation(
                        current => api.requestRedReview(contentUuid, current),
                        'Mayhem RED review requested.',
                    )}
                    onAnnounce={preamble => runMutation(
                        current => api.announce(contentUuid, current, preamble),
                        'Subscriber announcement queued.',
                    )}
                />}
                </details>
                <details className="content-settings-group">
                    <summary>Images &amp; videos</summary>
                {resource.isBlog && <FeaturedMedia
                    image={resource.image}
                    video={resource.video}
                    imageId={draft.imageId}
                    videoId={draft.videoId}
                    onChange={(field, value) => setDraft(current => ({...current, [field]: value}))}
                />}
                <ContentGallery
                    items={gallery}
                    onAdd={body => runMutation(current => api.gallery(
                        kind, contentUuid, current, 'POST', '', body,
                    ))}
                    onUpdate={(itemUuid, body) => runMutation(current => api.gallery(
                        kind, contentUuid, current, 'PATCH', `/${itemUuid}`, body,
                    ))}
                    onRemove={itemUuid => runMutation(current => api.gallery(
                        kind, contentUuid, current, 'DELETE', `/${itemUuid}`,
                    ))}
                    onOrder={order => runMutation(current => api.galleryOrder(
                        kind, contentUuid, current, order,
                    ))}
                />
                <YouTubeManager
                    items={youtube}
                    onAdd={body => runMutation(current => api.youtube(
                        kind, contentUuid, current, 'POST', '', body,
                    ))}
                    onRemove={itemUuid => runMutation(current => api.youtube(
                        kind, contentUuid, current, 'DELETE', `/${itemUuid}`,
                    ))}
                    onOrder={order => runMutation(current => api.youtubeOrder(
                        kind, contentUuid, current, order,
                    ))}
                />
                </details>
                <details className="content-settings-group">
                    <summary>Search &amp; sharing</summary>
                <MetadataEditor seo={draft.seo}
                    onChange={seo => setDraft(current => ({...current, seo}))} />
                </details>
                <details className="content-settings-group">
                    <summary>Design &amp; advanced options</summary>
                <DisplaySettingsSection
                    display={draft.display}
                    displayTemplates={displayTemplates}
                    onChangeDisplay={changeDisplay}
                />
                {usesMasthead && <MastheadImage
                    image={resource.mastheadImage}
                    imageId={draft.mastheadImageId}
                    isBlog={resource.isBlog}
                    onChange={mastheadImageId => setDraft(current => ({
                        ...current,
                        mastheadImageId,
                    }))}
                />}
                    <section className="content-editor-panel">
                        <h2>Page address &amp; HTML</h2>
                        <label htmlFor="content-slug">URL slug</label>
                        <input id="content-slug" type="text" value={draft.slug}
                            onChange={event => setDraft(current => ({...current, slug: event.target.value}))} />
                        <label htmlFor="html-policy">HTML policy</label>
                        <select id="html-policy" value={draft.content.htmlPolicy}
                            disabled={draft.display.template === 'customHtml'}
                            onChange={event => {
                                const value = event.target.value;
                                const warning = 'Advanced HTML permits limited classes, styles, and '
                                    + 'media tags. Scripts, forms, event handlers, and arbitrary embeds '
                                    + 'are still removed. Continue?';
                                if (value === 'advanced' && !window.confirm(warning)) return;
                                setDraft(current => ({
                                    ...current,
                                    ...(value === 'advanced' ? {confirmAdvancedHtml: true} : {}),
                                    content: {...current.content, htmlPolicy: value},
                                }));
                            }}>
                            {draft.content.htmlPolicy === 'takeover' && <option value="takeover">Full-page HTML (template)</option>}
                            <option value="standard">Standard HTML</option>
                            <option value="advanced">Advanced HTML</option>
                        </select>
                    </section>
                </details>
                <details className="content-settings-group">
                    <summary>More actions &amp; support</summary>
                {!resource.isBlog && resource.isPublished && <section
                    className="content-editor-panel" aria-labelledby="content-link-tree-title">
                    <h2 id="content-link-tree-title">Add to Link Tree</h2>
                    <form method="post" action="/account/landingpage/link/add"
                        encType="multipart/form-data" className="content-link-tree-form">
                        <input type="hidden" name="title" value={resource.title} />
                        <input type="hidden" name="url"
                            value={initialEnvelope.editor.storefrontUrl} />
                        <input type="hidden" name="after" value={backUrl} />
                        <button type="submit" className="button secondary">
                            Add this page to Link Tree
                        </button>
                    </form>
                </section>}
                <section className="content-editor-panel content-danger-zone"
                    aria-labelledby="content-danger-title">
                    <h2 id="content-danger-title">Delete {config.label.toLowerCase()}</h2>
                    <p>This permanently removes the content and cannot be undone.</p>
                    <button type="button" className="button danger" onClick={async () => {
                        if (!window.confirm(`Delete this ${config.label.toLowerCase()} permanently?`)) {
                            return;
                        }
                        setOperationError('');
                        try {
                            const saved = await saveNow();
                            await api.deleteContent(kind, contentUuid, saved?.etag || revision);
                            window.location.assign(backUrl);
                        } catch (nextError) {
                            setOperationError(nextError.message);
                        }
                    }}>Delete permanently</button>
                </section>
                <details className="content-support-details">
                    <summary>Support details</summary>
                    <dl>
                        <div><dt>Kind</dt><dd>{kind}</dd></div>
                        <div><dt>Format</dt><dd>{draft.content.format}</dd></div>
                        <div><dt>HTML policy</dt><dd>{draft.content.htmlPolicy}</dd></div>
                        <div><dt>Revision</dt><dd><code>{revision || 'unavailable'}</code></dd></div>
                    </dl>
                </details>
                {classicUrl && <p><a href={classicUrl}>Use classic editor</a></p>}
                </details>
            </div>

        </div>

        {resource.isBlog && <details className="content-settings-group">
            <summary>Comments</summary>
            <BlogComments
            comments={comments}
            meta={commentsMeta}
            onLoadMore={() => loadComments((commentsMeta.page || 1) + 1, true)}
            onReply={async (commentUuid, reply) => {
                await api.replyToComment(contentUuid, commentUuid, reply);
                await loadComments();
                setNotice('Reply posted.');
            }}
            onVisibility={async (commentUuid, visible) => {
                await api.setCommentVisibility(contentUuid, commentUuid, visible);
                setComments(current => current.map(comment => comment.uuid === commentUuid
                    ? {...comment, isVisible: visible}
                    : comment));
                setNotice(visible ? 'Comment shown.' : 'Comment hidden.');
            }}
        /></details>}

        <ConversionPreview
            conversion={conversion}
            onCancel={() => setConversion(null)}
            onConfirm={() => {
                const target = conversion;
                const writing = target.locale === defaultLanguage ? draft
                    : draft.translations[target.locale] || emptyTranslation;
                const key = target.locale === defaultLanguage && target.segment === 'secondary'
                    ? 'secondaryContent' : 'content';
                if (writing[key].content !== target.sourceContent) {
                    setOperationError('The draft changed while conversion was prepared. Try the conversion again.');
                    setConversion(null);
                    return;
                }
                setDraft(current => {
                    const translated = target.locale !== defaultLanguage;
                    const writing = translated ? current.translations[target.locale] || emptyTranslation : current;
                    const key = !translated && target.segment === 'secondary' ? 'secondaryContent' : 'content';
                    const updated = {...writing, [key]: {...writing[key], content: target.content,
                        format: target.format, preferredMode: target.mode}};
                    return translated ? {...current, translations: {
                        ...current.translations, [target.locale]: updated,
                    }} : updated;
                });
                setConversion(null);
            }}
        />
        <InlineMediaPicker
            isOpen={pickerOpen}
            pendingFile={pendingInlineFile}
            onCancel={() => {
                setPickerOpen(false);
                setPendingInlineFile(null);
            }}
            onInsert={insertInlineImage}
        />
        <PreviewModal url={previewUrl} onClose={() => setPreviewUrl('')} />
    </main>;
}

export default function ContentEditor({
    contentUuid,
    kind = 'page',
    backUrl = '',
    classicUrl = '',
    apiClient,
    mediaApiClient,
}) {
    const auth = useAuth();
    const api = useMemo(
        () => apiClient || createContentApi({fetch: auth.fetch, apiUrl: auth.API_URL}),
        [apiClient, auth.API_URL, auth.fetch],
    );
    const mediaApi = useMemo(
        () => mediaApiClient || createSellerMediaApi({fetch: auth.fetch, apiUrl: auth.API_URL}),
        [auth.API_URL, auth.fetch, mediaApiClient],
    );
    const [loaded, setLoaded] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        api.load(kind, contentUuid).then(result => {
            if (active) setLoaded({envelope: result.data, etag: result.etag});
        }).catch(nextError => {
            if (active) setError(nextError.message);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [api, contentUuid, kind, reloadKey]);

    if (!contentUuid) return <p className="flash error" role="alert">Content UUID is required.</p>;
    if (loading) return <p role="status">Loading {contentConfig(kind).label.toLowerCase()} editor…</p>;
    if (error) return <div className="flash error" role="alert">
        <p>{error}</p>
        <button type="button" className="button" onClick={() => setReloadKey(value => value + 1)}>
            Try again
        </button>
    </div>;
    if (!loaded) return null;
    return <ContentEditorLoaded
        key={`${contentUuid}:${reloadKey}`}
        api={api}
        mediaApi={mediaApi}
        kind={kind}
        contentUuid={contentUuid}
        initialEnvelope={loaded.envelope}
        initialEtag={loaded.etag}
        backUrl={backUrl}
        classicUrl={classicUrl}
        onReload={() => setReloadKey(value => value + 1)}
    />;
}
